import app from '../../dvaInstance'
import React from 'react'
import { isAppWebsite } from '../../Utils/helpers'
import { checkAuthorization } from '../Authorize/checkAuthorization'
import { cloneDeep } from 'lodash-es'
import { useLocationReference } from 'Utils/useLocationReference'

/**
 * Backend sends keys as seen on the left side
 * Frontend uses keys as seen on the right side
 * ```
 *  'ORG_DEPENDENT' = 'organisations',
 *  'FLOW_DEPENDENT' = 'flows',
 *  'INDEPENDENT' = 'independent',
 * ```
 */
enum PermissionsV2ScopeKeyMap {
  'ORG_DEPENDENT' = 'organisations',
  'FLOW_DEPENDENT' = 'flows',
  'INDEPENDENT' = 'independent',
}
type PermissionsV2ScopeKeys = keyof typeof PermissionsV2ScopeKeyMap

export function mapPermissionsV2ScopeKeys(permissions: {
  [key: string]: string[] | UserPolicyMap
}) {
  const permissionsWithUpdatedKeys = cloneDeep(permissions)
  for (const key in permissionsWithUpdatedKeys) {
    const isKeyValid = Object.prototype.hasOwnProperty.call(PermissionsV2ScopeKeyMap, key)
    if (isKeyValid) {
      const replacementKey = PermissionsV2ScopeKeyMap[key as PermissionsV2ScopeKeys]
      const value = permissionsWithUpdatedKeys[key]
      delete permissionsWithUpdatedKeys[key]
      permissionsWithUpdatedKeys[replacementKey] = value
    }
  }
  return permissionsWithUpdatedKeys
}

/**
 * Represents a location reference with flow UUID and organization UUID.
 * Based on these values, scope is decided.
 */
export interface LocationReference {
  flowUUID?: string
  organisationUUID?: string
}

/**
 * Represents the authorization details for a specific action or component.
 */
interface AuthorizationV2 {
  requiredPolicies: readonly string[]
  scopePriorityOrder?: Scope[]
  locationReference?: LocationReference
  /** Checks if required policies are present anywhere in the user permissions */
  anywhere?: boolean
  /** Whether to show debug logs or not, false by default */
  debug?: boolean
}

/**
 * Represents the possible scopes for Authorization
 */
type Scope = 'flows' | 'organisations' | 'independent'

interface UserPolicyMap {
  [key: string]: string[]
}

/**
 * Represents the user policies for different scopes.
 */
export interface UserPolicies {
  independent: string[]
  organisations: UserPolicyMap
  flows: UserPolicyMap
}

/**
 * Represents the user policies for different scopes.
 */
interface AuthorizationWrapperProps<T> extends AuthorizationV2 {
  /**
   * children components to be rendered, if authorized
   */
  children: any
  /**
   * requiredPermissions for Authorizing through IAM V1
   */
  reqPermissions?: string[]
  unauthorizedComponent?: React.ReactNode
}

/**
 * Authorizes the current scope based on the location reference, user policies, and scope priority order.
 * Just check, if the scope is valid or not.
 * @param {LocationReference} locationReference - The location reference for the authorization context.
 * @param {UserPolicies} userPolicies - The user policies for different scopes.
 * @param {Scope[]} scopePriorityOrder - The order of scope priority for authorization checking.
 * @returns {Object} An object containing authorization details.
 * @property {boolean} authorized - Indicates whether the current scope is authorized or not.
 * @property {Scope} scope - The authorized scope.
 * @property {string | null} uuid - The UUID associated with the authorized scope, or null if not applicable.
 */
const _authorizeCurrentScope = (
  locationReference: LocationReference | undefined,
  userPolicies: UserPolicies,
  scopePriorityOrder: Scope[]
): {
  authorized: boolean
  scope: Scope
  uuid: string | null
} => {
  // console.info({locationReference, userPolicies, scopePriorityOrder})
  for (const scope of scopePriorityOrder) {
    if (
      scope === 'flows' &&
      locationReference?.flowUUID &&
      userPolicies.flows &&
      userPolicies.flows[locationReference.flowUUID]
    ) {
      return { authorized: true, scope: 'flows', uuid: locationReference.flowUUID }
    }

    if (
      scope === 'organisations' &&
      // REVIEW: This is not required as locationReference might contain flowUUID
      // !locationReference.flowUUID &&
      locationReference?.organisationUUID &&
      userPolicies.organisations &&
      userPolicies.organisations[locationReference.organisationUUID]
    ) {
      return { authorized: true, scope: 'organisations', uuid: locationReference.organisationUUID }
    }

    if (
      scope === 'independent'
      // && !locationReference?.organisationUUID &&
      // !locationReference?.flowUUID
    ) {
      return { authorized: true, scope: 'independent', uuid: null }
    }
  }
  return { authorized: false, scope: 'independent', uuid: null }
}

/**
 * Checks if the required policies are applicable to the current
 * scope based on the available user policies.
 * This check is done based on an OR operation right now where
 * if any of the requiredPolicies are present in user policies the action is allowed.
 * @param {readonly string[]} requiredPolicies - The required policies to check.
 * @param {UserPolicies} userPolicies - The user policies for different scopes.
 * @param {Scope} authorizedScope - The authorized scope.
 * @param {string | null} scopeIdentifier - The identifier associated with the authorized scope.
 * @returns {boolean} Indicates whether the required policies are applicable or not.
 */
const _applicableCurrentScopePolicies = (
  requiredPolicies: readonly string[],
  userPolicies: UserPolicies,
  authorizedScope: Scope,
  scopeIdentifier: string | null
) => {
  // console.log("Identifying applicable User Policies")
  const applicableUserPolicies =
    authorizedScope === 'independent'
      ? userPolicies?.independent
      : userPolicies[authorizedScope][scopeIdentifier ?? '']

  return requiredPolicies.some((policy) => applicableUserPolicies?.includes(policy))
}

/**
 * Get the logger based on debug value
 * @param {boolean} debug
 * @returns Logger
 */
const getLogger = (debug = false) => {
  if (debug === true) {
    return console
  }
  return {
    log: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: () => undefined,
  }
}

/**
 * Checks if the user is authorized based on the provided authorization details.
 * @param {AuthorizationV2} props - The authorization details including required policies, scope priority order, and location reference.
 * @param {string[] | undefined} reqPermissions - The required permissions be used in IAM V1.
 * @returns {boolean} Indicates whether the user is authorized or not.
 */
const checkAuthorizationV2 = (
  props: AuthorizationV2,
  reqPermissions: string[] | undefined,
  policyLogicalOperator: 'and' | 'or' | undefined = 'and'
): boolean => {
  const logger = getLogger(props?.debug)

  const appName = isAppWebsite ? 'applications' : 'studio'
  // If the request is originated from "studio", use the existing checkAuthorization function.
  if (appName === 'studio') {
    logger.info(`Checking authorization for ${appName} using IAM V1`)
    return checkAuthorization(
      props.locationReference?.organisationUUID,
      reqPermissions || [],
      policyLogicalOperator
    )
  }
  logger.info(`Checking authorization for ${appName} using IAM V2`)

  if (props?.requiredPolicies?.length === 0) {
    logger.warn('Passed requiredPolicies is an Empty list, this will result in not authorized')
    return false
  }
  const userPolicies: UserPolicies = app._store.getState().auth.user.permissions_v2
  if (props?.anywhere === true) {
    return props?.requiredPolicies?.some((value) => JSON.stringify(userPolicies)?.includes(value))
  }
  const scopePriorityOrder: Scope[] = props.scopePriorityOrder ?? [
    'flows',
    'organisations',
    'independent',
  ]
  logger.info('ReqPol=========>', props?.requiredPolicies, props?.locationReference)
  const currentScopeAuthorized = _authorizeCurrentScope(
    props?.locationReference,
    userPolicies,
    scopePriorityOrder
  )
  let authorized = currentScopeAuthorized?.authorized ?? false
  const authorizedScope: Scope = currentScopeAuthorized.scope
  const scopeIdentifier: string | null = currentScopeAuthorized.uuid
  logger.info(
    'Result of Current Scope Authorization',
    currentScopeAuthorized,
    userPolicies,
    props?.requiredPolicies
  )

  authorized =
    authorized &&
    _applicableCurrentScopePolicies(
      props.requiredPolicies,
      userPolicies,
      authorizedScope,
      scopeIdentifier
    )
  logger.info('User Authorized: ', authorized)
  return authorized
}

/**
 * Higher-order component that wraps the children components and performs authorization checks based on the provided authorization props.
 * @template T - The type of the authorization props.
 * @param children: React.ReactNode to be rendered.
 * @param {AuthorizationWrapperProps<T>} authorizationProps - The authorization props including required policies, scope priority order, and location reference.
 * @returns {React.ReactNode | null} The wrapped children components if the user is authorized, otherwise null.
 */
const AuthorizationWrapperV2 = <T,>({
  children,
  unauthorizedComponent = null,
  ...authorizationProps
}: AuthorizationWrapperProps<T>) => {
  const { flowUUID, organisationUUID } = useLocationReference()
  // console.info("==>",{flow_uuid, organisation_uuid, requiredPermissions: authorizationProps?.reqPermissions})
  const locationReference: LocationReference = {
    flowUUID: authorizationProps.locationReference?.flowUUID ?? flowUUID,
    organisationUUID: authorizationProps.locationReference?.organisationUUID ?? organisationUUID,
  } as LocationReference

  const isAuthorized = checkAuthorizationV2(
    { ...authorizationProps, locationReference },
    authorizationProps?.reqPermissions
  )

  if (isAuthorized) {
    return children
  }
  return unauthorizedComponent
}

export { checkAuthorizationV2, AuthorizationWrapperV2, PermissionsV2ScopeKeyMap }
